import React, { createContext, useContext, useState, useCallback, useEffect } from "react";

const LanguageContext = createContext(null);

// Resolve a possibly-bilingual value ({it,en}) to the current language string.
const resolve = (v, lang) => {
  if (v && typeof v === "object" && !Array.isArray(v) && ("it" in v || "en" in v)) {
    return v[lang] ?? v.it ?? v.en ?? "";
  }
  return v;
};

const DICT = {
  it: {
    "nav.home": "HOME",
    "nav.about": "SALTY FOLKS",
    "nav.lessons": "LEZIONI",
    "nav.prices": "PREZZI",
    "nav.gallery": "GALLERY",
    "nav.events": "EVENTI",
    "nav.location": "DOVE SIAMO",
    "nav.contact": "CONTATTI",
    "cta.bookNow": "PRENOTA",
    "lessons.eyebrow": "Surf Lessons",
    "lessons.title": "Trova la tua onda",
    "lessons.sub.pre": "Ogni lezione dura",
    "lessons.sub.duration": "1 ora e 20 minuti",
    "lessons.sub.post": ". Attrezzatura e istruttori certificati inclusi.",
    "lessons.onRequest": "Su richiesta",
    "prices.eyebrow": "Prezzi",
    "prices.title": "Listino chiaro",
    "prices.book": "PRENOTA ORA",
    "gallery.eyebrow": "Gallery",
    "gallery.title": "Vita da Salty Folks",
    "gallery.all": "Tutte",
    "location.eyebrow": "Dove Siamo",
    "location.directions": "GET DIRECTIONS",
    "contact.eyebrow": "Contatti",
    "contact.title": "Scrivici, ci vediamo in acqua",
    "contact.sub": "Hai domande sulle lezioni o sulla disponibilità? Contattaci direttamente — rispondiamo in giornata.",
    "contact.whatsapp": "WhatsApp",
    "contact.phone": "Telefono",
    "contact.email": "Email",
    "contact.instagram": "Instagram",
    "events.eyebrow": "Eventi",
    "events.title": "Prossimi eventi",
    "events.subtitle": "Esperienze speciali firmate Salty Folks.",
    "events.date": "Data",
    "events.time": "Orario",
    "events.place": "Luogo",
    "events.book": "PRENOTA",
    "events.empty": "Nessun evento in programma al momento. Torna presto!",
    "footer.contacts": "CONTATTI",
    "footer.social": "SOCIAL",
    "footer.legal": "LEGAL",
    "footer.privacy": "Privacy Policy",
    "footer.cookie": "Cookie Policy",
    "footer.admin": "Gestione",
    "book.home": "HOME",
    "book.title": "Prenota la tua lezione",
    "book.durationNote": "Ogni lezione dura 1 ora e 20 minuti (80 min)",
    "book.step.lesson": "Lezione",
    "book.step.datetime": "Data & Ora",
    "book.step.participants": "Partecipanti",
    "book.step.details": "Dati",
    "book.step.confirm": "Conferma",
    "book.chooseDate": "Scegli la data",
    "book.availableTimes": "Orari disponibili",
    "book.loading": "Caricamento...",
    "book.noSlots": "Nessuno slot disponibile per questa data.",
    "book.participants": "Numero di partecipanti",
    "book.firstName": "Nome",
    "book.lastName": "Cognome",
    "book.phone": "Telefono / WhatsApp",
    "book.notes": "Note (opzionale)",
    "book.summary": "Riepilogo prenotazione",
    "book.back": "Indietro",
    "book.continue": "Continua",
    "book.confirm": "CONFERMA PRENOTAZIONE",
    "book.sending": "Invio...",
    "book.sum.lesson": "Lezione",
    "book.sum.date": "Data",
    "book.sum.time": "Ora",
    "book.sum.participants": "Partecipanti",
    "book.sum.name": "Nome",
    "book.sum.email": "Email",
    "book.sum.phone": "Telefono",
    "book.sum.notes": "Note",
    "book.confirmed.title": "Prenotazione confermata!",
    "book.confirmed.sub": "Ci vediamo in acqua. Riceverai conferma ai contatti indicati.",
    "book.confirmed.home": "TORNA ALLA HOME",
    "book.onRequest": "Su richiesta",
  },
  en: {
    "nav.home": "HOME",
    "nav.about": "SALTY FOLKS",
    "nav.lessons": "SURF LESSONS",
    "nav.prices": "PRICES",
    "nav.gallery": "GALLERY",
    "nav.events": "EVENTS",
    "nav.location": "LOCATION",
    "nav.contact": "CONTACT",
    "cta.bookNow": "BOOK NOW",
    "lessons.eyebrow": "Surf Lessons",
    "lessons.title": "Find your wave",
    "lessons.sub.pre": "Each lesson lasts",
    "lessons.sub.duration": "1 hour and 20 minutes",
    "lessons.sub.post": ". Gear and certified instructors included.",
    "lessons.onRequest": "On request",
    "prices.eyebrow": "Prices",
    "prices.title": "Clear pricing",
    "prices.book": "BOOK NOW",
    "gallery.eyebrow": "Gallery",
    "gallery.title": "Life at Salty Folks",
    "gallery.all": "All",
    "location.eyebrow": "Location",
    "location.directions": "GET DIRECTIONS",
    "contact.eyebrow": "Contact",
    "contact.title": "Get in touch, see you in the water",
    "contact.sub": "Questions about lessons or availability? Contact us directly — we reply the same day.",
    "contact.whatsapp": "WhatsApp",
    "contact.phone": "Phone",
    "contact.email": "Email",
    "contact.instagram": "Instagram",
    "events.eyebrow": "Events",
    "events.title": "Upcoming events",
    "events.subtitle": "Special experiences by Salty Folks.",
    "events.date": "Date",
    "events.time": "Time",
    "events.place": "Location",
    "events.book": "BOOK",
    "events.empty": "No events scheduled right now. Check back soon!",
    "footer.contacts": "CONTACTS",
    "footer.social": "SOCIAL",
    "footer.legal": "LEGAL",
    "footer.privacy": "Privacy Policy",
    "footer.cookie": "Cookie Policy",
    "footer.admin": "Management",
    "book.home": "HOME",
    "book.title": "Book your lesson",
    "book.durationNote": "Each lesson lasts 1 hour and 20 minutes (80 min)",
    "book.step.lesson": "Lesson",
    "book.step.datetime": "Date & Time",
    "book.step.participants": "Participants",
    "book.step.details": "Details",
    "book.step.confirm": "Confirm",
    "book.chooseDate": "Choose the date",
    "book.availableTimes": "Available times",
    "book.loading": "Loading...",
    "book.noSlots": "No slots available for this date.",
    "book.participants": "Number of participants",
    "book.firstName": "First name",
    "book.lastName": "Last name",
    "book.phone": "Phone / WhatsApp",
    "book.notes": "Notes (optional)",
    "book.summary": "Booking summary",
    "book.back": "Back",
    "book.continue": "Continue",
    "book.confirm": "CONFIRM BOOKING",
    "book.sending": "Sending...",
    "book.sum.lesson": "Lesson",
    "book.sum.date": "Date",
    "book.sum.time": "Time",
    "book.sum.participants": "Participants",
    "book.sum.name": "Name",
    "book.sum.email": "Email",
    "book.sum.phone": "Phone",
    "book.sum.notes": "Notes",
    "book.confirmed.title": "Booking confirmed!",
    "book.confirmed.sub": "See you in the water. You'll receive confirmation at the contacts provided.",
    "book.confirmed.home": "BACK TO HOME",
    "book.onRequest": "On request",
  },
};

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("sf_lang") || "it");

  const setLang = useCallback((l) => {
    setLangState(l);
    localStorage.setItem("sf_lang", l);
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback((key) => (DICT[lang] && DICT[lang][key]) ?? key, [lang]);
  const L = useCallback((v) => resolve(v, lang), [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, L }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLang = () => useContext(LanguageContext);
